import { css } from '@emotion/css';
import { Clickable } from 'dg-web-shared/ui/Clickable';
import { languages } from '../../layout/i18n/Languages';
import { PrivacyLink } from '../../legal/PrivacyPolicyFiles';
import { Colors } from 'dg-web-shared/ui/vars';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { TosLink } from '../../legal/TosFiles';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { type JSX, useState } from 'react';
import { languageFromString } from 'dg-web-shared/lib/Localized.ts';

export enum LandingPageAnchor {
    app = 'anchor-app',
    faq = 'anchor-faq',
}

export function LandingPageFooter() {
    const [languageSwitchOpen, setLanguageSwitchOpen] = useState(false);

    return (
        <div
            className={css({
                height: '48px',
                display: 'flex',
                flexShrink: 0,
                flexGrow: 0,
                position: 'relative',
                backgroundImage: Colors.blackGradient,
                alignItems: 'center',
            })}
        >
            {!languageSwitchOpen && (
                <>
                    <Privacy />
                    <Tos />
                </>
            )}
            <LanguageSwitch
                languageSwitchOpen={languageSwitchOpen}
                setLanguageSwitchOpen={setLanguageSwitchOpen}
            />
        </div>
    );
}

function Privacy() {
    return (
        <FooterItem>
            <PrivacyLink>
                <Localized
                    de="Privacy"
                    fr="Privacy"
                    it="Privacy"
                    en="Privacy"
                />
            </PrivacyLink>
        </FooterItem>
    );
}

function Tos() {
    return (
        <FooterItem>
            <TosLink>
                <Localized de="AGB" fr="CGV" it="CG" en="GTC" />
            </TosLink>
        </FooterItem>
    );
}

const FooterItem = (p: {
    children?: React.ReactNode;
    onClick?: () => void;
}) => {
    return (
        <Clickable
            element="div"
            onClick={p.onClick}
            className={css({
                color: Colors.white,
                fontSize: '13px',
                ...Typo.robotoRegular,
                height: '48px',
                lineHeight: '48px',
                marginLeft: '20px',
                a: {
                    color: Colors.white,
                    fontSize: '13px',
                    ...Typo.robotoRegular,
                    textDecoration: 'none',
                },
            })}
        >
            {p.children}
        </Clickable>
    );
};

function LanguageSwitch({
    languageSwitchOpen,
    setLanguageSwitchOpen,
}: {
    languageSwitchOpen: boolean;
    setLanguageSwitchOpen: (open: boolean) => void;
}): JSX.Element {
    const { language, setLanguage } = useLanguage();

    return (
        <div
            className={css({
                position: 'absolute',
                right: '20px',
                display: 'flex',
                height: '30px',
                border: `1px solid ${Colors.white}`,
                borderRadius: '2px',
                background: Colors.blackGradient,
            })}
        >
            {languages.map(l => {
                const isCurrent = language === l.shortcut;
                if (languageSwitchOpen || isCurrent)
                    return (
                        <Clickable
                            element="div"
                            className={css({
                                fontSize: '14px',
                                letterSpacing: '1px',
                                lineHeight: '30px',
                                textAlign: 'center',
                                width: '50px',
                                ...Typo.robotoLight,
                                color:
                                    languageSwitchOpen && isCurrent
                                        ? Colors.blackGradient
                                        : Colors.white,
                                backgroundColor:
                                    languageSwitchOpen && isCurrent
                                        ? Colors.white
                                        : Colors.blackGradient,
                            })}
                            onClick={() => {
                                if (languageSwitchOpen) {
                                    setLanguage(languageFromString(l.shortcut));
                                    setLanguageSwitchOpen(false);
                                } else {
                                    setLanguageSwitchOpen(true);
                                }
                            }}
                            key={l.shortcut}
                        >
                            {l.shortcut.toLocaleUpperCase()}
                        </Clickable>
                    );
                return null;
            })}
        </div>
    );
}
